// MARK://dhk8/pos/pages/MenuBrowsePage
// /menu — read-only menu browser (Sprint B; reworked Sprint UI-B to 534×738 portrait).
// FastBar-style pill category tabs + compact 2-col product grid (no big image box);
// tapping a product opens the store-style ProductDetailDialog (photo, description,
// variations, stock badge, trend, "más vendido"). (Adding products to an order
// happens on OrderPage in Sprint C.)

import { useMemo, useState } from 'react'
import { Ban, Check } from 'lucide-react'
import { formatCents, type ProductWithStock } from '@dockethub/ui-restaurant'
import { useActiveMenu, useSetProductAvailability } from '../hooks/useCatalog.js'
import { t } from '../i18n/es-MX.js'
import ProductDetailDialog from '../features/ProductDetailDialog.js'

const ALL = 'ALL'

export default function MenuBrowsePage() {
  const menu = useActiveMenu()
  const [detail, setDetail] = useState<ProductWithStock | null>(null)
  const [activeCat, setActiveCat] = useState<string>(ALL)

  // 86 / agotar producto: toggle de disponibilidad en servicio. El backend
  // (`setProductAvailability` → Product.active) no se refleja en `menuScreen`
  // todavía (no expone `active`), así que mantenemos un override optimista local
  // por productId: una vez que el cajero apaga/enciende un producto, el badge
  // "Agotado" se actualiza al instante y persiste durante la sesión de la vista.
  const setAvail = useSetProductAvailability()
  const [availOverride, setAvailOverride] = useState<Record<string, boolean>>({})
  const [availBusyId, setAvailBusyId] = useState<string | null>(null)
  async function toggleAvailability(productId: string, currentlyAvailable: boolean) {
    const next = !currentlyAvailable
    setAvailOverride((m) => ({ ...m, [productId]: next })) // optimista
    setAvailBusyId(productId)
    try {
      const res = await setAvail.mutateAsync({ productId, available: next })
      setAvailOverride((m) => ({ ...m, [productId]: res.active }))
    } catch {
      setAvailOverride((m) => ({ ...m, [productId]: currentlyAvailable })) // revertir
    } finally {
      setAvailBusyId(null)
    }
  }

  const products = menu.data?.products ?? []
  const categories = useMemo(() => {
    const inUse = new Set(products.map((p) => p.categoryId).filter(Boolean) as string[])
    return (menu.data?.categories ?? []).filter((c) => inUse.has(c.id)).slice().sort((a, b) => a.sortOrder - b.sortOrder)
  }, [menu.data, products])

  const filtered = useMemo(
    () => (activeCat === ALL ? products : products.filter((p) => p.categoryId === activeCat)),
    [products, activeCat],
  )

  return (
    <div className="dhk8-pos-menupage">
      <div className="dhk8-pos-menupage__head">
        <h1 className="dhk8-pos-menupage__h1">{t('menu.title')}</h1>
        <span className="dhk8-pos-menupage__hint">{t('menu.browseHint')}</span>
      </div>

      <div className="dhk8-pos-menupage__cats" role="tablist" aria-label="Categorías">
        <button type="button" role="tab" aria-selected={activeCat === ALL} className={`dhk8-pos-menupage__cat${activeCat === ALL ? ' dhk8-pos-menupage__cat--active' : ''}`} onClick={() => setActiveCat(ALL)}>Todos</button>
        {categories.map((c) => (
          <button key={c.id} type="button" role="tab" aria-selected={activeCat === c.id} className={`dhk8-pos-menupage__cat${activeCat === c.id ? ' dhk8-pos-menupage__cat--active' : ''}`} onClick={() => setActiveCat(c.id)}>{c.name}</button>
        ))}
      </div>

      <div className="dhk8-pos-menupage__grid">
        {menu.isLoading && <div className="dhk8-pos-menupage__empty">Cargando menú…</div>}
        {menu.isError && <div className="dhk8-pos-menupage__empty">{menu.error instanceof Error ? menu.error.message : t('menu.loadError')}</div>}
        {!menu.isLoading && !menu.isError && filtered.length === 0 && <div className="dhk8-pos-menupage__empty">No hay productos disponibles.</div>}
        {!menu.isLoading && filtered.map((p) => {
          // El override del "86" (si existe) manda sobre el isAvailable del menú.
          const overridden = availOverride[p.id]
          const available86 = overridden !== undefined ? overridden : p.isAvailable !== false
          const eightySixed = !available86
          const off = eightySixed || p.stockStatus === 'OUT'
          const low = p.stockStatus === 'LOW'
          const busy = availBusyId === p.id
          return (
            <div key={p.id} className="dhk8-pos-menupage__cardwrap">
              <button type="button" className={`dhk8-pos-menupage__card${off ? ' dhk8-pos-menupage__card--off' : ''}`} onClick={() => setDetail(p)}
                aria-label={`${p.name} — ${formatCents(p.unitPriceCents)}${eightySixed ? ' — agotado' : ''}`}>
                <span className="dhk8-pos-menupage__card-thumb" aria-hidden="true">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt="" loading="lazy" className="dhk8-pos-menupage__card-img" />
                  ) : (
                    <span className="dhk8-pos-menupage__card-initial">{p.name.trim().slice(0, 1).toUpperCase()}</span>
                  )}
                  {off && <span className="dhk8-pos-menupage__badge dhk8-pos-menupage__badge--off dhk8-pos-menupage__badge--overlay">Agotado</span>}
                  {low && !off && <span className="dhk8-pos-menupage__badge dhk8-pos-menupage__badge--low dhk8-pos-menupage__badge--overlay">Bajo</span>}
                </span>
                <span className="dhk8-pos-menupage__card-name">{p.name}</span>
                <span className="dhk8-pos-menupage__card-foot">
                  <span className="dhk8-pos-menupage__card-sku">{p.sku}</span>
                  <span className="dhk8-pos-menupage__card-price">{formatCents(p.unitPriceCents)}</span>
                </span>
              </button>
              {/* 86 / agotar: toggle de disponibilidad. Hermano de la card-button (no
                  anidado) para HTML válido + accesible. */}
              <button
                type="button"
                className={`dhk8-pos-menupage__avail${eightySixed ? ' dhk8-pos-menupage__avail--off' : ''}`}
                disabled={busy}
                aria-label={eightySixed ? `Reactivar ${p.name}` : `Agotar ${p.name} (86)`}
                aria-pressed={eightySixed}
                title={eightySixed ? 'Marcar disponible de nuevo' : 'Marcar como agotado (86)'}
                onClick={() => { if (!busy) void toggleAvailability(p.id, available86) }}
              >
                {eightySixed ? <Check size={13} /> : <Ban size={13} />}
              </button>
            </div>
          )
        })}
      </div>

      <ProductDetailDialog product={detail} onClose={() => setDetail(null)} />

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-pos-menupage { display:flex; flex-direction:column; gap:var(--pos-space-3,12px); min-height:0; flex:1; }
  .dhk8-pos-menupage__head { display:flex; align-items:baseline; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-pos-menupage__h1 { margin:0; font-family:var(--font-display,system-ui); font-size:var(--pos-text-2xl,24px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-pos-menupage__hint { font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); letter-spacing:.04em; }

  .dhk8-pos-menupage__cats { flex-shrink:0; display:flex; gap:var(--pos-space-2,8px); overflow-x:auto; padding-bottom:var(--pos-space-1,4px); }
  .dhk8-pos-menupage__cat { padding:var(--pos-space-2,8px) var(--pos-space-4,16px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); letter-spacing:.01em; cursor:pointer; white-space:nowrap; border-radius:var(--pos-radius-pill,999px); min-height:var(--pos-touch-target,44px); transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-pos-menupage__cat:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-pos-menupage__cat--active { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }

  .dhk8-pos-menupage__grid { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); grid-auto-rows:minmax(min-content,max-content); gap:var(--pos-space-3,12px); align-content:start; padding-bottom:var(--pos-space-4,16px); }
  .dhk8-pos-menupage__empty { grid-column:1/-1; padding:var(--pos-space-6,24px); text-align:center; font-size:var(--pos-text-md,15px); color:var(--pos-fg-tertiary,#7E8C82); }

  .dhk8-pos-menupage__cardwrap { position:relative; display:flex; }
  .dhk8-pos-menupage__card { flex:1; min-width:0; display:grid; grid-template-rows:auto 1fr auto; gap:8px; padding:var(--pos-space-2,8px); background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); color:var(--pos-fg,#E8EFE9); text-align:left; cursor:pointer; transition:transform var(--pos-duration-fast,120ms) ease, background var(--pos-duration-fast,120ms) ease; overflow:hidden; }
  .dhk8-pos-menupage__card:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-pos-menupage__card:active { transform:scale(.98); background:var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-pos-menupage__card--off { opacity:.45; }
  .dhk8-pos-menupage__card-thumb { position:relative; display:block; width:100%; aspect-ratio:1/1; background:var(--pos-bg,#0E1311); border-radius:var(--pos-radius-sm,6px); overflow:hidden; }
  .dhk8-pos-menupage__card-img { width:100%; height:100%; object-fit:cover; display:block; }
  .dhk8-pos-menupage__card-initial { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-family:var(--font-display,system-ui); font-size:clamp(28px,5vw,40px); font-weight:600; color:var(--pos-fg-tertiary,#7E8C82); background:linear-gradient(135deg,rgba(255,255,255,.04),rgba(255,255,255,0)); }
  .dhk8-pos-menupage__badge--overlay { position:absolute; top:6px; right:6px; backdrop-filter:blur(6px); }
  /* 86 / agotar toggle — botón redondo en la esquina sup. izq. del thumb.
     Default = "Agotar" (icono prohibido). Cuando ya está agotado el botón
     muestra el check verde para "reactivar". Opacidad plena de la card-off no
     lo afecta porque vive dentro del thumb (que también se atenúa, OK). */
  .dhk8-pos-menupage__avail { position:absolute; top:6px; left:6px; z-index:2; width:30px; height:30px; padding:0; display:flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-pill,999px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:color-mix(in oklch, var(--pos-surface,#161C18) 70%, transparent); backdrop-filter:blur(6px); color:var(--pos-fg-secondary,#B6BFB8); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease, color var(--pos-duration-fast,120ms) ease; }
  .dhk8-pos-menupage__avail:hover:not(:disabled) { background:var(--pos-danger-bg,rgba(229,72,77,.16)); color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 45%, transparent); }
  .dhk8-pos-menupage__avail:focus-visible { outline:2px solid var(--pos-tint,#1F8B3A); outline-offset:2px; }
  .dhk8-pos-menupage__avail:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-pos-menupage__avail--off { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-pos-menupage__avail--off:hover:not(:disabled) { background:var(--pos-tint,#1F8B3A); color:#fff; filter:brightness(1.08); }
  .dhk8-pos-menupage__card-name { font-family:var(--font-display,system-ui); font-size:var(--pos-text-sm,13px); line-height:1.25; color:var(--pos-fg,#E8EFE9); display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:2.5em; }
  .dhk8-pos-menupage__card-sku { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); letter-spacing:.02em; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-pos-menupage__card-foot { display:flex; align-items:baseline; justify-content:space-between; gap:var(--pos-space-2,8px); }
  .dhk8-pos-menupage__card-price { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-sm,13px); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); }
  .dhk8-pos-menupage__badge { font-size:var(--pos-text-xxs,11px); padding:1px var(--pos-space-2,8px); border-radius:var(--pos-radius-pill,999px); font-weight:var(--pos-weight-medium,500); }
  .dhk8-pos-menupage__badge--off { background:var(--pos-danger-bg,rgba(229,72,77,.16)); color:var(--pos-danger,#E5484D); }
  .dhk8-pos-menupage__badge--low { background:var(--pos-warning-bg,rgba(241,179,59,.16)); color:var(--pos-warning,#F1B33B); }
  @media (max-width: 520px) { .dhk8-pos-menupage__grid { grid-template-columns:1fr; } }
`
